import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Ticket, Plus, Save, Trash2, Power, PowerOff, Loader2, AlertCircle, BadgePercent, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
    fetchAdminCoupons, createCoupon, updateCoupon, deleteCoupon,
} from "@/lib/adminApi";

const emptyCoupon = () => ({
    id: null,
    code: "",
    percent: 10,
    description: "",
    active: true,
});

export default function AdminCoupons() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(null);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchAdminCoupons();
            setCoupons(Array.isArray(data.items) ? data.items : []);
        } catch (_e) {
            toast.error("Could not load coupons");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const sorted = useMemo(
        () => [...coupons].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
        [coupons]
    );

    const openNew = () => { setDraft(emptyCoupon()); setEditing(null); };
    const openEdit = (c) => { setDraft({ ...emptyCoupon(), ...c }); setEditing(c.id); };
    const closeForm = () => { setDraft(null); setEditing(null); };

    const onSubmit = async (payload) => {
        const code = (payload.code || "").trim().toUpperCase();
        const percent = Number(payload.percent);
        if (!code) { toast.error("Coupon code is required"); return; }
        if (!(percent >= 1 && percent <= 100)) { toast.error("Percent must be between 1 and 100"); return; }
        const body = { code, percent, description: payload.description || "", active: !!payload.active };
        try {
            if (editing) {
                const updated = await updateCoupon(editing, body);
                setCoupons((arr) => arr.map((x) => (x.id === editing ? updated : x)));
                toast.success("Coupon updated");
            } else {
                const created = await createCoupon(body);
                setCoupons((arr) => [created, ...arr]);
                toast.success("Coupon created");
            }
            closeForm();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Could not save", { description: typeof detail === "string" ? detail : "Please retry." });
        }
    };

    const onToggleActive = async (c) => {
        try {
            const updated = await updateCoupon(c.id, { active: !c.active });
            setCoupons((arr) => arr.map((x) => (x.id === c.id ? updated : x)));
        } catch (_e) { toast.error("Could not update"); }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this coupon? Clients can no longer redeem it.")) return;
        try {
            await deleteCoupon(id);
            setCoupons((arr) => arr.filter((x) => x.id !== id));
            toast.success("Deleted");
            if (editing === id) closeForm();
        } catch (_e) {
            toast.error("Could not delete");
        }
    };

    return (
        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="pg-eyebrow inline-flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5" /> Studio CMS · Coupons
                    </span>
                    <h1
                        className="font-display mt-4 text-3xl sm:text-4xl"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="admin-coupons-headline"
                    >
                        Discount Coupons
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                        Percentage codes clients can apply on the Review step of the Package journey. Codes are
                        case-insensitive; the discount applies to the whole estimated total (package + add-ons).
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openNew}
                    className="pg-btn-primary pg-app-tap"
                    data-testid="admin-coupons-add"
                >
                    <Plus className="h-3.5 w-3.5" /> New Coupon
                </button>
            </div>

            <AnimatePresence>
                {draft && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mt-6"
                    >
                        <CouponEditor draft={draft} isNew={!editing} onSubmit={onSubmit} onCancel={closeForm} />
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="mt-6">
                {loading ? (
                    <div
                        className="flex items-center justify-center rounded-2xl border px-6 py-20 text-sm"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                    >
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading coupons…
                    </div>
                ) : sorted.length === 0 ? (
                    <div
                        className="rounded-2xl border px-6 py-14 text-center"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                        data-testid="admin-coupons-empty"
                    >
                        <AlertCircle className="mx-auto mb-2 h-5 w-5" />
                        <p className="text-sm">No coupons yet. Click <strong>New Coupon</strong> to create your first code.</p>
                    </div>
                ) : (
                    <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="admin-coupons-list">
                        {sorted.map((c) => (
                            <CouponRow
                                key={c.id}
                                coupon={c}
                                onEdit={() => openEdit(c)}
                                onDelete={() => onDelete(c.id)}
                                onToggleActive={() => onToggleActive(c)}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

const CouponEditor = ({ draft, isNew, onSubmit, onCancel }) => {
    const [data, setData] = useState(draft);
    useEffect(() => { setData(draft); }, [draft]);
    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
    const submit = (e) => { e.preventDefault(); onSubmit(data); };

    return (
        <form onSubmit={submit} className="pg-app-surface p-5 sm:p-7" data-testid="admin-coupons-editor">
            <h2 className="font-display text-xl sm:text-2xl" style={{ color: "var(--pg-fg)" }}>
                {isNew ? "New Coupon" : "Edit Coupon"}
            </h2>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <Field label="Coupon Code *" testId="admin-coupons-code">
                    <input
                        type="text"
                        required
                        value={data.code}
                        onChange={(e) => set("code", e.target.value.toUpperCase())}
                        className="pg-input"
                        style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                        placeholder="PGFX20"
                    />
                </Field>
                <Field label="Discount Percent (1–100) *" testId="admin-coupons-percent">
                    <div className="relative">
                        <input
                            type="number"
                            min={1}
                            max={100}
                            required
                            value={data.percent}
                            onChange={(e) => set("percent", e.target.value)}
                            className="pg-input pr-9"
                            placeholder="20"
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--pg-fg-muted)" }}>%</span>
                    </div>
                </Field>
            </div>

            <Field label="Description (optional)" testId="admin-coupons-description" className="mt-4">
                <input
                    type="text"
                    value={data.description}
                    onChange={(e) => set("description", e.target.value)}
                    className="pg-input"
                    placeholder="Launch offer — 20% off any package"
                />
            </Field>

            <div className="mt-5 flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--pg-fg)" }}>
                    <input type="checkbox" checked={data.active !== false} onChange={(e) => set("active", e.target.checked)} data-testid="admin-coupons-active" />
                    Active (clients can redeem)
                </label>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
                <button type="submit" className="pg-btn-primary pg-app-tap" data-testid="admin-coupons-save">
                    <Save className="h-4 w-4" /> {isNew ? "Create Coupon" : "Save Changes"}
                </button>
                <button type="button" onClick={onCancel} className="pg-btn-ghost pg-app-tap" data-testid="admin-coupons-cancel">
                    Cancel
                </button>
            </div>
        </form>
    );
};

const CouponRow = ({ coupon, onEdit, onDelete, onToggleActive }) => {
    const inactive = coupon.active === false;
    return (
        <li
            className="rounded-2xl border p-4 sm:p-5"
            style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)", opacity: inactive ? 0.6 : 1 }}
            data-testid="admin-coupons-row"
            data-coupon-id={coupon.id}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span
                            className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 font-display text-base font-bold tracking-wide"
                            style={{ borderColor: "color-mix(in srgb, var(--pg-accent) 40%, var(--pg-border))", background: "color-mix(in srgb, var(--pg-accent) 12%, transparent)", color: "var(--pg-fg)" }}
                        >
                            <Ticket className="h-3.5 w-3.5" style={{ color: "var(--pg-accent)" }} /> {coupon.code}
                        </span>
                        <span
                            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold"
                            style={{ background: "color-mix(in srgb, var(--pg-accent) 16%, transparent)", color: "var(--pg-accent)" }}
                        >
                            <BadgePercent className="h-3.5 w-3.5" /> {Math.round(coupon.percent)}% off
                        </span>
                        {inactive && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
                                style={{ borderColor: "var(--pg-border-strong)", background: "var(--pg-surface-2)", color: "var(--pg-fg-muted)" }}
                            >
                                <PowerOff className="h-3 w-3" /> Disabled
                            </span>
                        )}
                    </div>
                    {coupon.description && (
                        <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--pg-fg-muted)" }}>
                            {coupon.description}
                        </p>
                    )}
                </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
                <button type="button" onClick={onEdit} className="pg-btn-ghost pg-app-tap" data-testid={`admin-coupons-edit-${coupon.id}`}>
                    Edit
                </button>
                <button type="button" onClick={onToggleActive} className="pg-btn-ghost pg-app-tap" data-testid={`admin-coupons-toggle-${coupon.id}`}>
                    {inactive ? <Power className="h-3.5 w-3.5" /> : <PowerOff className="h-3.5 w-3.5" />}
                    {inactive ? "Enable" : "Disable"}
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs transition-colors hover:bg-[var(--pg-surface-2)]"
                    style={{ borderColor: "var(--pg-border)", color: "#E55353" }}
                    data-testid={`admin-coupons-delete-${coupon.id}`}
                >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
            </div>
        </li>
    );
};

const Field = ({ label, children, testId, className }) => (
    <div className={`flex flex-col gap-1.5 ${className || ""}`} data-testid={testId}>
        <span className="text-[11px] font-semibold tracking-normal" style={{ color: "var(--pg-fg-muted)" }}>{label}</span>
        {children}
    </div>
);
